import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { ganttService } from '../api/gantt.service';
import { tasksToGanttModel } from '../adapters';
import type { GanttGroupLabels } from '../adapters';
import { serializeFilters, serializeSort } from '../utils/filter-serializer';
import { TASK_KEYS } from './queryKeys';
import { TASK_COLLECTION_STALE_TIME } from './taskQueryConfig';
import type {
  GanttGroupBy,
  GanttModel,
  TaskFilters,
  TaskSummaryDto,
  GanttDependencyDto,
} from '../types';

interface UseGanttModelParams {
  projectId: string;
  boardId?: string;
  filters: TaskFilters;
  groupBy: GanttGroupBy;
  showCompleted: boolean;
}

interface UseGanttModelResult {
  model: GanttModel;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
}

/**
 * Orchestrates the two queries behind the Gantt — the rich rows (`view=gantt`)
 * and the dependency edges — and folds them into a single normalized
 * {@link GanttModel}. `showCompleted` and `groupBy` are client-side transforms,
 * so toggling them re-derives the model without refetching.
 */
export function useGanttModel({
  projectId,
  boardId,
  filters,
  groupBy,
  showCompleted,
}: UseGanttModelParams): UseGanttModelResult {
  const { t } = useTranslation('tasks');
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const serializedFilters = serializeFilters(filters);

  const {
    data: rawRows,
    isLoading: rowsLoading,
    isError: rowsError,
  } = useQuery({
    queryKey: TASK_KEYS.gantt(orgId ?? '', projectId, boardId, serializedFilters, filters.search),
    queryFn: async ({ signal }): Promise<TaskSummaryDto[]> => {
      const all: TaskSummaryDto[] = [];
      let cursor: string | undefined = undefined;
      for (let page = 0; page < 100; page++) {
        const result = await tasksService.getQuery(
          projectId,
          {
            view: 'gantt',
            limit: 100,
            sort: serializeSort({ columnId: 'startDate', direction: 'asc' }),
            ...(boardId && { boardId }),
            ...(serializedFilters && { filters: serializedFilters }),
            ...(filters.search && { search: filters.search }),
            ...(cursor && { cursor }),
          },
          signal,
        );
        all.push(...(result.items ?? []));
        if (!result.nextCursor) return all;
        cursor = result.nextCursor;
      }
      return all; // safety bound hit
    },
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId,
  });

  const {
    data: depsData,
    isLoading: depsLoading,
    isError: depsError,
  } = useQuery({
    queryKey: TASK_KEYS.ganttDependencies(orgId ?? '', projectId),
    queryFn: (): Promise<GanttDependencyDto[]> => ganttService.getDependencies(projectId),
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId,
  });

  const labels: GanttGroupLabels = {
    unassigned: t('gantt.unassigned'),
    noGroup: t('gantt.noGroup'),
    priority: {
      urgent: t('priority.urgent'),
      high: t('priority.high'),
      medium: t('priority.medium'),
      low: t('priority.low'),
      none: t('priority.none'),
    },
  };

  const allTasks = rawRows ?? [];
  const visibleTasks = showCompleted ? allTasks : allTasks.filter((task) => !task.isCompleted);
  const model = tasksToGanttModel(visibleTasks, depsData ?? [], groupBy, labels);

  const isLoading = rowsLoading || depsLoading;
  const isError = rowsError || depsError;

  return {
    model,
    isLoading,
    isError,
    isEmpty: !isLoading && !isError && model.rows.length === 0,
  };
}
